import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useAsync } from 'react-use';
import {
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
} from 'victory';

import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useService,
} from '../../../../../../hooks';
import MedicalResultsService, {
  ResultHistory,
  ResultHistoryWithRange,
} from '../../../../../../services/MedicalResultsService';
import { MeasurementCode } from '../../types';
import { getColorByStatus } from '../../util/graphUtils';

const medicalResultsService = new MedicalResultsService();

interface SparklineProps extends React.HTMLAttributes<HTMLDivElement> {
  dimension: MeasurementCode;
  patient: fhir.Patient;
}

export const Sparkline: React.FC<SparklineProps> = ({ dimension, patient }) => {
  const cohort = useActiveProjectCohortId();
  const activeProject = useActiveProjectId();

  const [fetchMedicalResultsHistory] = useService(
    medicalResultsService.getHistory,
  );

  const patientId = patient.id as string;
  const dimensionCode = dimension.codes[0].key as string;
  const dimensionSystem = dimension.codes[0].system as string;

  const { value } = useAsync(async () => {
    const response = await fetchMedicalResultsHistory({
      patientId,
      project: activeProject,
      code: dimensionCode,
      system: dimensionSystem,
      cohort,
      includeClinicOnly: true,
    });
    return response.data;
  }, [patientId, dimensionCode, dimensionSystem, cohort, activeProject]);

  const medicalResults = useMemo(
    () => value?.items.filter(_ => _.resource?.status === 'final'),
    [value?.items],
  );

  return <Graph medicalResults={medicalResults} />;
};

interface GraphProps {
  medicalResults?: ResultHistory[];
}

type Status = ResultHistoryWithRange['result']['status'] | undefined;

type DataPoint = {
  x: number;
  y: number;
  delta: number;
  status: Status;
  unit: string;
};

function Graph({ medicalResults }: GraphProps): React.ReactElement | null {
  const data: DataPoint[] = useMemo(() => {
    if (!medicalResults) return [];

    const data = medicalResults
      .map(r => {
        return {
          value: r.resource?.valueQuantity?.value,
          date: DateTime.fromISO(r.resource?.effectiveDateTime || ''),
          status: r.result?.status as Status,
          unit: r.resource?.valueQuantity?.unit || '',
        };
      })
      .filter(
        (
          r,
        ): r is {
          value: number;
          date: DateTime;
          status: Status;
          unit: string;
        } => r.value !== undefined && r.date.isValid,
      )
      .sort((a, b) => a.date.toMillis() - b.date.toMillis())
      .map((r, index, arr) => {
        return {
          x: index,
          y: r.value,
          delta:
            Math.round((r.value - (arr[index - 1]?.value || 0)) * 100) / 100,
          status: r.status,
          unit: r.unit,
        };
      });

    return data;
  }, [medicalResults]);

  if (data.length < 2) return null;

  return (
    <VictoryChart
      theme={{
        ...VictoryTheme.material,
        axis: {
          ...VictoryTheme.material.axis,
          style: {
            ...VictoryTheme.material.axis?.style,
            axis: { stroke: 'transparent' },
            grid: { stroke: 'transparent' },
            ticks: { stroke: 'transparent' },
            tickLabels: { fill: 'transparent' },
          },
        },
      }}
      scale={{ x: 'linear' }}
      padding={{ right: 200, top: 80 }}
      domain={{
        x: [0, data.length],
        y: [
          Math.min(...data.map(d => d.y || 0)) * 0.8,
          Math.max(...data.map(d => d.y || 0)) * 1.2,
        ],
      }}
    >
      <VictoryLine
        style={{
          data: {
            stroke: 'black',
            strokeWidth: 4,
          },
        }}
        data={data}
        interpolation="linear"
      />
      <VictoryScatter
        data={[data.at(-1)]}
        size={8}
        style={{
          data: {
            fill: ({ datum }) => getLabelColor(datum),
            stroke: 'white',
            strokeWidth: 2,
          },
        }}
        labels={({ datum }: { datum: DataPoint }) => [
          datum.delta > 0
            ? `⏶ +${datum.delta}`
            : datum.delta < 0
            ? `⏷ -${Math.abs(datum.delta)}`
            : '',
          `${datum.y} ${datum.unit}`,
        ]}
        labelComponent={
          <VictoryLabel
            lineHeight={1.2}
            style={[
              {
                fill: ({ datum }) => getLabelColor(datum),
                fontSize: 36,
              },
              { fill: '#333333', fontSize: 32 },
            ]}
            textAnchor="start"
            dx={10}
            dy={-20}
            verticalAnchor="end"
          />
        }
      />
    </VictoryChart>
  );
}

function getLabelColor(data: DataPoint): string {
  if (data.status === undefined) return 'black';
  return getColorByStatus(data.status);
}
